import { supabase, callFunction } from '../lib/supabase';

const PLAN_DEFINITIONS = {
  core: {
    programType: 'CORE',
    name: 'Fitnession Core',
    programName: 'Fitnession Core',
    description: 'Complete lifestyle transformation with diet, workouts & coaching',
    monthlyPrice: 2999,
    originalPrice: 3999,
    isPopular: true,
    badge: 'MOST POPULAR',
    features: [
      'Personalized Meal Plans', 'Custom Workout Programs', '1:1 Health Coaching',
      'Progress Tracking App', 'Nutrition Guidance', 'Email & Chat Support',
      'Weekly Check-ins', 'Lifestyle Coaching',
    ],
  },
  medical: {
    programType: 'MEDICAL',
    name: 'Fitnession Medical Care',
    programName: 'Fitnession Medical Care',
    description: 'Doctor-supervised program for medical conditions',
    monthlyPrice: 5999,
    originalPrice: 7999,
    isPopular: false,
    badge: 'DOCTOR SUPERVISED',
    features: [
      'Everything in Core, plus:', 'Doctor + Coach Joint Management',
      'Medical Condition Monitoring', 'Safe Exercise Programs',
      'Regular Health Check-ups', 'Medication Management Support',
      'Priority 24/7 Support', 'Mental Health Support',
    ],
  },
  glp1: {
    programType: 'GLP1',
    name: 'GLP-1 Complete Program',
    programName: 'GLP-1 Complete Program',
    description: 'Comprehensive GLP-1 medication program with full medical supervision',
    monthlyPrice: 9999,
    originalPrice: 13999,
    isPopular: false,
    badge: 'GLP-1 PROGRAM',
    features: [
      'GLP-1 Medication Management', 'Dedicated Doctor Supervision',
      'Personalized Nutrition Plan', 'Weekly Medical Check-ins',
      'Side Effect Monitoring', 'Health Coach Support',
      'Lab Work Monitoring', 'Lifestyle Optimization',
    ],
  },
};

const DURATION_DISCOUNTS = { 1: 0, 3: 10, 6: 20, 12: 30 };

export const getPlans = async () =>
  Object.entries(PLAN_DEFINITIONS).map(([id, p]) => ({
    id,
    name: p.name,
    description: p.description,
    price: p.monthlyPrice,
    originalPrice: p.originalPrice,
    currency: 'INR',
    durationMonths: 1,
    programType: p.programType,
    features: p.features,
    isPopular: p.isPopular,
    discountPercentage: 0,
    badge: p.badge,
  }));

export const getPlanById = async (planId) => {
  const plan = PLAN_DEFINITIONS[planId];
  if (!plan) throw new Error(`Unknown plan: ${planId}`);
  return { id: planId, ...plan, price: plan.monthlyPrice };
};

export const getProgramWithDurations = async (programId) => {
  const plan = PLAN_DEFINITIONS[programId];
  if (!plan) throw new Error(`Unknown program: ${programId}`);

  const durationOptions = Object.entries(DURATION_DISCOUNTS).map(([months, discountPct]) => {
    const m = Number(months);
    const discountedMonthly = Math.round(plan.monthlyPrice * (1 - discountPct / 100));
    const totalPrice = discountedMonthly * m;
    const originalTotal = plan.originalPrice * m;
    const savings = originalTotal - totalPrice;
    let badge = null;
    if (m === 6) badge = 'MOST POPULAR';
    else if (m === 12) badge = 'BEST VALUE';
    return {
      planId: programId,
      durationMonths: m,
      monthlyPrice: discountedMonthly,
      totalPrice,
      originalPrice: originalTotal,
      savings,
      discountPercentage: discountPct,
      badge,
      label: m === 1 ? '1 Month' : `${m} Months`,
    };
  });

  return {
    id: programId,
    programType: plan.programType,
    programName: plan.programName,
    description: plan.description,
    features: plan.features,
    durationOptions,
  };
};

// Create Razorpay order — edge function (server secret required)
export const createOrder = async (planId, durationMonths = 1) =>
  callFunction('razorpay-create-order', { planId, durationMonths });

// Verify payment — edge function (HMAC signature verification)
export const verifyPayment = async (razorpayOrderId, razorpayPaymentId, razorpaySignature) =>
  callFunction('razorpay-verify-payment', {
    razorpayOrderId,
    razorpayPaymentId,
    razorpaySignature,
  });

// Mark payment as failed in DB
export const handlePaymentFailure = async (orderId, reason) => {
  await supabase
    .from('payments')
    .update({ status: 'FAILED', failure_reason: reason })
    .eq('razorpay_order_id', orderId);
};

export const getPaymentHistory = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
};

export const getCurrentSubscription = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', user.id)
    .eq('status', 'ACTIVE')
    .order('created_at', { ascending: false })
    .limit(1)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const getSubscriptionHistory = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
};

export const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export const initializeRazorpayCheckout = async (orderData, onSuccess, onError, onDismiss) => {
  const scriptLoaded = await loadRazorpayScript();
  if (!scriptLoaded) throw new Error('Failed to load Razorpay SDK');

  const options = {
    key: process.env.REACT_APP_RAZORPAY_KEY_ID || orderData.razorpayKeyId,
    amount: orderData.amountPaise,   // in paise, already set by edge function
    currency: orderData.currency || 'INR',
    name: 'Fitnession',
    description: orderData.planName,
    order_id: orderData.orderId,
    prefill: { name: orderData.userName || '', email: orderData.userEmail || '' },
    notes: { plan_id: orderData.planId },
    theme: { color: '#10b981' },
    modal: { ondismiss: () => onDismiss?.() },
    handler: async (response) => {
      try {
        const result = await verifyPayment(
          response.razorpay_order_id,
          response.razorpay_payment_id,
          response.razorpay_signature
        );
        onSuccess?.(result);
      } catch (err) { onError?.(err); }
    },
  };

  const razorpay = new window.Razorpay(options);
  razorpay.on('payment.failed', async (response) => {
    try {
      await handlePaymentFailure(response.error.metadata.order_id, response.error.description);
    } catch (e) { console.error('Failed to record payment failure:', e); }
    onError?.(response.error);
  });
  razorpay.open();
};

export default {
  getPlans, getPlanById, getProgramWithDurations, createOrder, verifyPayment,
  handlePaymentFailure, getPaymentHistory, getCurrentSubscription,
  getSubscriptionHistory, loadRazorpayScript, initializeRazorpayCheckout,
};
